'use client';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { BuyeasyDialog } from '@/components/modals/BuyeasyDialog';
import { MessageBox, MessageBoxProps } from '@/components/modals/MessageBox';

// allows to show a modal window even when there's no host component to render it
// if need be, can be easily updated to store a stack of modal windows

interface ModalInfo {
  content: ReactNode;
}

export interface ModalContextProps {
  showDialog: (modalInfo: ModalInfo) => void;
  closeModal: () => void;
  showModal: (modalProps: MessageBoxProps) => void;
  setGlass: () => void;
  removeGlass?: () => void;
}

const ModalContext = createContext<ModalContextProps>({
  showDialog: () => {},
  closeModal: () => {},
  showModal: () => {},
  setGlass: () => {},
});

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider = ({ children }: ModalProviderProps) => {
  const [modalInfo, setModalInfo] = useState<ModalInfo | null>(null);

  // shows a component in a popup window, with a gray background
  const showDialog = useCallback((mi: ModalInfo) => {
    setModalInfo(mi);
  }, []);

  // removes the popup window
  const closeModal = useCallback(() => {
    setModalInfo(null);
  }, []);

  const showModal = useCallback<ModalContextProps['showModal']>(
    (modalProps) => {
      setModalInfo({
        content: (
          <MessageBox
            {...modalProps}
            onClick={(button) => {
              if (button.autoClose !== false) closeModal();
            }}
          />
        ),
      });
    },
    [closeModal],
  );

  // shows just a gray background, to prevent user from clicking anywhere while a network operation is executing
  const setGlass = useCallback(() => {
    setModalInfo({ content: null });
  }, []);

  // removes the gray background
  const removeGlass = useCallback(() => {
    setModalInfo(null);
  }, []);

  const providedValue = useMemo(() => {
    return {
      showDialog,
      closeModal,
      showModal,
      setGlass,
      removeGlass,
    };
  }, [showDialog, closeModal, showModal, setGlass, removeGlass]);

  return (
    <ModalContext.Provider value={providedValue}>
      {children}
      {modalInfo && <BuyeasyDialog content={modalInfo.content} />}
    </ModalContext.Provider>
  );
};

ModalProvider.displayName = 'ModalProvider';

export const useModalContext = () => useContext(ModalContext);
